function parseThreshold(value, fallback = 0) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.min(1, Math.max(0, parsed));
}

function parseBoolean(value, fallback = false) {
  if (value === undefined || value === null) {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return value === 'true' || value === '';
}

function parseTriggers(element) {
  const config = element.dataset.inView;

  if (config) {
    try {
      const parsed = JSON.parse(config);

      if (!Array.isArray(parsed)) {
        throw new TypeError('data-in-view must be a JSON array');
      }

      return parsed.map((trigger) => ({
        threshold: parseThreshold(trigger.threshold, 0),
        className: trigger.class,
        target: trigger.target ?? null,
        toggle: Boolean(trigger.toggle),
        invert: Boolean(trigger.invert),
        triggered: false,
      }));
    } catch (error) {
      console.warn('[InView] Invalid data-in-view config', element, error);
      return [];
    }
  }

  if (element.dataset.inViewClass) {
    return [{
      threshold: parseThreshold(element.dataset.inViewThreshold, 0),
      className: element.dataset.inViewClass,
      target: element.dataset.inViewTarget ?? null,
      toggle: parseBoolean(element.dataset.inViewToggle),
      invert: parseBoolean(element.dataset.inViewInvert),
      triggered: false,
    }];
  }

  return [];
}

function getTargetElement(root, selector) {
  if (!selector) {
    return root;
  }

  return root.querySelector(selector);
}

function getIntersectionRatio(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

  if (rect.width <= 0 || rect.height <= 0) {
    return 0;
  }

  const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
  const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);

  if (visibleHeight <= 0 || visibleWidth <= 0) {
    return 0;
  }

  return (visibleHeight * visibleWidth) / (rect.height * rect.width);
}

class InView {
  constructor(element) {
    if (!(element instanceof HTMLElement)) {
      throw new TypeError('Invalid element');
    }

    this.element = element;
    this.triggers = parseTriggers(element);
    this.onDetailsToggle = this.onDetailsToggle.bind(this);

    if (!this.triggers.length) {
      return;
    }

    const thresholds = [...new Set(this.triggers.map((trigger) => trigger.threshold))].sort((a, b) => a - b);

    this.observer = this.createObserver(
      element.dataset.inViewRootMargin ?? '0px',
      thresholds,
    );

    this.observer.observe(element);

    // Closed <details> content is display:none, so IntersectionObserver often
    // misses the reveal. Re-check when a parent accordion opens.
    this.details = element.closest('details');
    this.details?.addEventListener('toggle', this.onDetailsToggle);
  }

  // rootMargin only accepts px/%, and an invalid value throws. Without this
  // guard one bad config would abort the loop that builds every other instance.
  createObserver(rootMargin, threshold) {
    const callback = this.intersectionCallback.bind(this);

    try {
      return new IntersectionObserver(callback, { root: null, rootMargin, threshold });
    } catch (error) {
      console.warn('[InView] Invalid data-in-view-root-margin', this.element, error);
      return new IntersectionObserver(callback, { root: null, threshold });
    }
  }

  applyTriggers(ratio, isIntersecting = true) {
    this.triggers.forEach((trigger) => {
      const meets = isIntersecting && ratio >= trigger.threshold;
      const shouldApply = trigger.invert ? !meets : meets;
      const target = getTargetElement(this.element, trigger.target);

      if (!target || !trigger.className) {
        return;
      }

      if (trigger.toggle) {
        target.classList.toggle(trigger.className, shouldApply);
        return;
      }

      if (trigger.triggered || !shouldApply) {
        return;
      }

      target.classList.add(trigger.className);
      trigger.triggered = true;
    });
  }

  intersectionCallback(entries) {
    entries.forEach((entry) => {
      const hasToggle = this.triggers.some((trigger) => trigger.toggle);

      if (!entry.isIntersecting && !hasToggle) {
        return;
      }

      this.applyTriggers(entry.intersectionRatio, entry.isIntersecting);
    });
  }

  onDetailsToggle() {
    if (!this.details?.open) {
      return;
    }

    requestAnimationFrame(() => {
      const ratio = getIntersectionRatio(this.element);
      this.applyTriggers(ratio, ratio > 0);
    });
  }

  destroy() {
    this.details?.removeEventListener('toggle', this.onDetailsToggle);
    this.observer?.disconnect();
  }
}

export default InView;
